<template>
    <div class="gauge-container" ref="container">
        <canvas ref="canvas"></canvas>
        <div class="text">
            <p class="big-text">
                <span class="font-45">{{ tyresAmount }}</span>&nbsp;
                <span class="font-35" :class="props.titleClass ? props.titleClass : 'text-uppercase'">{{ $t('points.board.remaining') }}</span>
            </p>
            <p class="fw-bold">
                {{ goalLabelText }}:
                <span class="achieved">{{ remaining }}</span> / {{ target }}
            </p>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, defineProps, onBeforeUnmount, computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps({
    achieved: Number,
    target: Number,
    remaining: Number,
    titleClass: String,
    goalLabel: String,
});

const safeTarget = computed(() => Math.max(1, props.target || 0));
const goalLabelText = computed(() => props.goalLabel ?? t("points.board.yourGoal"));
const tyresAmount = computed(() => props.target - props.remaining < 0 ? 0 : props.target - props.remaining)

const canvas = ref(null);
const container = ref(null);
const animatedAchieved = ref(0);
const size = ref(300);
let resizeObserver;

const updateSize = () => {
    if (container.value) {
        size.value = container.value.clientWidth;
        drawGauge(animatedAchieved.value / props.target);
    }
};

const animateGauge = (finalValue) => {
    const capped = Math.min(finalValue || 0, safeTarget.value);
    let start = 0;
    let startTime = null;
    const duration = 1500;

    const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        const currentValue = start + progress * (capped - start);

        animatedAchieved.value = Math.round(currentValue);
        drawGauge(currentValue / props.target);

        if (progress < 1) {
            requestAnimationFrame(animate);
        }
    };

    requestAnimationFrame(animate);
};

const drawGauge = (progress) => {
    if (!canvas.value) return;
    const ctx = canvas.value.getContext("2d");
    const width = size.value;
    const height = width / 2;
    const centerX = width / 2;
    const centerY = height;
    const radius = width / 2 - 15;

    canvas.value.width = width;
    canvas.value.height = height;

    ctx.clearRect(0, 0, width, height);

    const startAngle = Math.PI;
    const endAngle = 2 * Math.PI;
    const progressAngle = startAngle + progress * Math.PI;

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, startAngle, endAngle);
    ctx.strokeStyle = "#ddd";
    ctx.lineWidth = 25;
    ctx.lineCap = "round";
    ctx.stroke();

    ctx.beginPath();
    ctx.setLineDash([12, 8]);
    ctx.lineCap = "round";
    ctx.arc(centerX, centerY, radius, startAngle, endAngle);
    ctx.strokeStyle = "#4CAF50";
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.setLineDash([]);

    if (progress < 1) {
        ctx.beginPath();
        ctx.lineCap = "round";
        ctx.arc(centerX, centerY, radius, progressAngle, endAngle);
        ctx.strokeStyle = "#F2790D";
        ctx.lineWidth = 25;
        ctx.stroke();
    }

    const needleX = centerX + radius * Math.cos(progressAngle);
    const needleY = centerY + radius * Math.sin(progressAngle);
    drawArrow(ctx, centerX, centerY, needleX, needleY);
};

const drawArrow = (ctx, baseX, baseY, tipX, tipY) => {
    const angle = Math.atan2(tipY - baseY, tipX - baseX) + Math.PI / 4;
    const svgOriginalHeight = 160;
    const desiredLength = Math.sqrt((tipX - baseX) ** 2 + (tipY - baseY) ** 2);
    const scaleFactor = desiredLength / svgOriginalHeight;

    ctx.save();

    ctx.translate(baseX, baseY);
    ctx.rotate(angle);
    ctx.scale(scaleFactor, scaleFactor);

    ctx.translate(-8.35, -111);

    ctx.beginPath();
    ctx.moveTo(8.35087, 110.779);
    ctx.bezierCurveTo(8.19687, 110.779, 8.04287, 110.774, 7.88787, 110.763);
    ctx.lineTo(7.27787, 110.721);
    ctx.bezierCurveTo(5.47687, 110.597, 3.83287, 109.777, 2.65087, 108.411);
    ctx.bezierCurveTo(1.46887, 107.047, 0.890864, 105.304, 1.02486, 103.504);
    ctx.bezierCurveTo(1.16086, 101.664, 2.05887, 99.942, 3.48887, 98.778);
    ctx.lineTo(115.088, 7.88403);
    ctx.lineTo(13.0909, 108.829);
    ctx.bezierCurveTo(11.8219, 110.086, 10.1389, 110.777, 8.35188, 110.777);
    ctx.closePath();
    ctx.fillStyle = "black";
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(107.391, 14.797);
    ctx.lineTo(12.737, 108.475);
    ctx.bezierCurveTo(11.562, 109.638, 10.004, 110.279, 8.35096, 110.279);
    ctx.bezierCurveTo(8.20796, 110.279, 8.06497, 110.274, 7.92097, 110.264);
    ctx.lineTo(7.31096, 110.222);
    ctx.bezierCurveTo(5.64296, 110.107, 4.12195, 109.348, 3.02695, 108.085);
    ctx.bezierCurveTo(1.93195, 106.822, 1.39696, 105.208, 1.52096, 103.541);
    ctx.bezierCurveTo(1.64696, 101.839, 2.47796, 100.244, 3.80196, 99.1671);
    ctx.lineTo(107.39, 14.798);
    ctx.fillStyle = "#309D6D";
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(122.781, 0.973045);
    ctx.lineTo(3.17196, 98.39);
    ctx.bezierCurveTo(1.62996, 99.646, 0.671954, 101.482, 0.524954, 103.466);
    ctx.bezierCurveTo(0.228954, 107.466, 3.24196, 110.943, 7.24296, 111.219);
    ctx.lineTo(7.85296, 111.261);
    ctx.bezierCurveTo(8.01896, 111.272, 8.18596, 111.278, 8.35096, 111.278);
    ctx.bezierCurveTo(10.25, 111.278, 12.081, 110.53, 13.44, 109.185);
    ctx.lineTo(122.78, 0.972046);
    ctx.closePath();
    ctx.fillStyle = "#309D6D";
    ctx.fill();

    ctx.restore();
};

onMounted(() => {
    animateGauge(props.achieved);
    resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(container.value);
    updateSize();
});

onBeforeUnmount(() => {
    if (resizeObserver) {
        resizeObserver.disconnect();
    }
});

watch(() => props.achieved, (newVal) => animateGauge(newVal));
</script>


<style scoped lang="scss">
@import "@/styles/gauge_chart.scss";
</style>
